import {
    AdditionalColumnConfigFieldsFragment,
    useGetAdditionalColumnConfigsQuery
} from "generated/graphql";
import { groupBy } from "lodash";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useCluster } from "./ClusterContext";
import { useCurrentScenarioMetadata } from "./ScenarioPageContext";

export type AdditionalColumnTargetTable =
    | "scenario_projects"
    | "scenario_constraints"
    | "scenario_allocated_upgrades";

type ConfigsByTable = Readonly<
    Record<
        AdditionalColumnTargetTable,
        ReadonlyArray<AdditionalColumnConfigFieldsFragment>
    >
>;

const EMPTY_CONFIGS: ConfigsByTable = {
    scenario_projects: [],
    scenario_constraints: [],
    scenario_allocated_upgrades: []
};

const AdditionalColumnConfigsContext =
    createContext<ConfigsByTable>(EMPTY_CONFIGS);

export const AdditionalColumnConfigsProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const { id: clusterId } = useCluster();
    const { stage: scenarioStage } = useCurrentScenarioMetadata();
    const { data } = useGetAdditionalColumnConfigsQuery({
        variables: { clusterId, scenarioStage }
    });

    const value = useMemo<ConfigsByTable>(() => {
        const configs = data?.additional_column_configs ?? [];
        const grouped = groupBy(configs, (c) => c.target_table);
        return {
            scenario_projects: grouped["scenario_projects"] ?? [],
            scenario_constraints: grouped["scenario_constraints"] ?? [],
            scenario_allocated_upgrades:
                grouped["scenario_allocated_upgrades"] ?? []
        };
    }, [data]);

    return (
        <AdditionalColumnConfigsContext.Provider value={value}>
            {children}
        </AdditionalColumnConfigsContext.Provider>
    );
};

export const useAdditionalColumnsForTable = (
    targetTable: AdditionalColumnTargetTable
): ReadonlyArray<AdditionalColumnConfigFieldsFragment> => {
    return useContext(AdditionalColumnConfigsContext)[targetTable];
};
